import { getSuggestionsMachine } from '~/apps/search-wizard/inversify.config'
import { SuggestionsMachineInterpret } from '~/apps/search-wizard/campsites-search/location/machine/suggestionsMachine'
import { LocationWizardData } from '~/apps/search-wizard/domain/LocationWizardData'
import { waitForInterpreterState } from '~/utility/xstate/waitForInterpreterState'

export const useLocationSuggestionsStore = defineStore(
  'locationSuggestionsStore',
  () => {
    const stateStrings = ref<Array<string>>([])
    const context = ref<SuggestionsMachineInterpret['state']['context'] | null>(
      null,
    )

    const suggestionsMachine = getSuggestionsMachine(
      (useRoute().params.lang as string) || 'en-gb',
    )

    suggestionsMachine.onTransition((state) => {
      stateStrings.value = state.toStrings()
      context.value = state.context
    })

    const input = computed(() => context?.value?.input)
    const suggestions = computed(() => context?.value?.suggestions)

    async function getSuggestionsFor(input) {
      suggestionsMachine.send({
        type: 'SET_INPUT',
        input,
      })
      await waitForInterpreterState({
        machine: suggestionsMachine,
        successStates: ['idle'],
        errorStates: ['error'],
      })
      return suggestions
    }

    function waitForMachineIdle() {
      return waitForInterpreterState({
        machine: suggestionsMachine,
        successStates: ['idle'],
        errorStates: ['error'],
      })
    }

    async function dispatchAsyncLocationSelection() {
      await waitForMachineIdle()
      const suggestions = getMappedSuggestions(context.value?.suggestions)
      return suggestions[0]
    }

    function getMappedSuggestions(suggestions): LocationWizardData[] {
      let suggestionsFormatted: LocationWizardData[] = []
      if (suggestions.hierarchies) {
        suggestionsFormatted = suggestionsFormatted.concat(
          suggestions.hierarchies.map((suggestion) => {
            return { ...suggestion, ...{ dataType: 'hierarchy' } }
          }),
        )
      }
      if (suggestions.campsites) {
        suggestionsFormatted = suggestionsFormatted.concat(
          suggestions.campsites.map((suggestion) => {
            return { ...suggestion, ...{ dataType: 'campsite' } }
          }),
        )
      }
      if (suggestions.custom) {
        suggestionsFormatted = suggestionsFormatted.concat(
          suggestions.custom.map((suggestion) => {
            return { ...suggestion, ...{ dataType: 'custom' } }
          }),
        )
      }
      if (suggestions.recentSearches) {
        suggestionsFormatted = suggestionsFormatted.concat(
          suggestions.recentSearches.map((suggestion) => {
            return { ...suggestion, ...{ dataType: 'recentSearch' } }
          }),
        )
      }
      return suggestionsFormatted
    }

    async function loadGeoPointForLocation(
      location: Extract<LocationWizardData, { dataType: 'custom' }>,
    ) {
      suggestionsMachine.send({
        type: 'LOAD_POINT_FOR_CUSTOM',
        mapboxId: location.mapboxId,
      })

      await waitForMachineIdle()

      const suggestionWithPoint = context.value?.suggestions?.custom?.find(
        (suggestion) => suggestion.mapboxId === location.mapboxId,
      )

      if (!suggestionWithPoint) {
        throw new Error(
          'useLocationSuggestionsStore loadGeoPointForLocation: suggestionWithPoint not found',
        )
      }

      return suggestionWithPoint
    }

    return {
      context,
      input,
      suggestions,
      suggestionsMachine,
      stateStrings,
      waitForMachineIdle,
      getSuggestionsFor,
      dispatchAsyncLocationSelection,
      loadGeoPointForLocation,
      send: suggestionsMachine.send,
    }
  },
)
